import axios from 'axios';
import httpClient from './httpClient';
import {
  GetAllAdminListingsResponse,
  GetAdminListingByIdResponse,
  AdminListingListItem,
  AdminListingDetail,
} from '../types/adminlistingsmodel';

/**
 * Fetches all listings for the admin panel via
 * GET /provider/listings/admin/list.
 * Returns the listings array on success, throws an Error on failure.
 */
export const getAllAdminListings = async (): Promise<AdminListingListItem[]> => {
  try {
    const { data } = await httpClient.get<GetAllAdminListingsResponse>(
      '/provider/listings/admin/list'
    );
    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch listings.');
    }
    return data.result.listings;
  } catch (err) {
    if (axios.isAxiosError<GetAllAdminListingsResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to fetch listings.');
    }
    throw err;
  }
};

/**
 * Fetches a single listing's full details (including provider and
 * location-type info) via GET /provider/listings/admin/list/:id.
 * Returns the AdminListingDetail on success, throws an Error on failure.
 */
export const getAdminListingById = async (id: string): Promise<AdminListingDetail> => {
  try {
    const { data } = await httpClient.get<GetAdminListingByIdResponse>(
      `/provider/listings/admin/list/${id}`
    );
    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch listing details.');
    }
    return data.result;
  } catch (err) {
    if (axios.isAxiosError<GetAdminListingByIdResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to fetch listing details.');
    }
    throw err;
  }
};

/**
 * Allowed review decisions for the admin approval form — these are the
 * exact string values the approval PUT endpoint expects.
 */
export type ListingApprovalStatus = 'approved' | 'rejected' | 'changes_requested';

export interface UpdateListingApprovalPayload {
  status: ListingApprovalStatus;
  adminNote?: string;
}

interface UpdateListingApprovalResponse {
  success: boolean;
  message: string;
  errors: string[];
  result: null;
}

/**
 * Updates a listing's approval status (and optional admin note) via
 * PUT /provider/listings/admin/:id/approval.
 * Resolves on success, throws an Error (with the API message) on failure.
 */
export const updateListingApproval = async (
  id: string,
  payload: UpdateListingApprovalPayload
): Promise<void> => {
  try {
    const { data } = await httpClient.put<UpdateListingApprovalResponse>(
      `/provider/listings/admin/${id}/approval`,
      payload
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to update listing status.');
    }
  } catch (err) {
    if (axios.isAxiosError<UpdateListingApprovalResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to update listing status.');
    }
    throw err;
  }
};